<template>
  <div
    class="design_cockpit"
    :class="{ 'has-fixed-actionbar': showFixedActionBar }"
  >
    <div class="design_cockpit__heading">
      <h1 class="h1--underline">
        {{ $tv('Frontend.ORing.ExpertMode', 'ORingConfiguratorImport') }}
      </h1>
      <div>
        <NavigationClose
          @click="showClosePopup = true"
        />
      </div>
    </div>
    <Steps
      class="design_cockpit__steps"
      :steps="steps"
      :current-step="designCockpitCurrentStep"
      :completed-steps="designCockpitCompletedSteps"
      @go-to-step="goToStep($event)"
    />
    <div class="design_cockpit__subheading">
      <h2>
        {{ steps[designCockpitCurrentStep - 1].title }}
      </h2>
      <div
        class="toggle-switches"
        v-show="designCockpitCurrentStep === 3 || designCockpitCurrentStep === 4"
      >
        <ToggleSwitchSquare
          :is-checked="oringCalculatorImperial"
          :label-right="tv('inch', 'ORingConfigurator')"
          :label-left="tv('mm', 'ORingConfigurator')"
          @change-value="(value) => oringStore.performUnitLengthSwitch(value)"
        />
      </div>
    </div>
    <component
      :is="steps[designCockpitCurrentStep - 1].component"
      ref="stepComponentRef"
      class="comp-slot"
    />
    <div
      class="mobile-buttons-wrapper"
      :class="{ 'is-fixed': showFixedActionBar }"
    >
      <div
        class="buttons"
        :class="{'flex-end': designCockpitCurrentStep === 1}"
      >
        <ReactiveButton
          v-show="designCockpitCurrentStep > 1"
          :theme-color="'light'"
          :button-text="$tv('Frontend.Wizard.PreviousButtonText', 'ORingConfiguratorImport')"
          @click="goToStep(designCockpitCurrentStep - 1)"
        />
        <ReactiveButton
          v-if="designCockpitCurrentStep===steps.length"
          @click="showStartPopup = true"
          :is-primary-button="true"
          :button-text="$tv('startNewSearch', 'ORingConfigurator')"
        />
        <ReactiveButton
          v-else
          :theme-color="'base'"
          :button-text="nextButtonText"
          :disabled="isNextButtonDisabled"
          @click="handleNext()"
        />
      </div>
    </div>
    <PopupConfirmDialog
      :show="showClosePopup"
      :css-class="'prompt--discard'"
      :size="POPUP.Size.small"
      :headline-text="tv('discardPromptTitle', 'ORingConfigurator')"
      :text="tv('Frontend.Wizard.LeaveDescription', 'ORingConfiguratorImport')"
      :cancel-button="tv('Frontend.Wizard.Stay', 'ORingConfiguratorImport')"
      :confirmation-button="tv('Frontend.Wizard.Leave', 'ORingConfiguratorImport')"
      @close-pop-up="showClosePopup = false"
      @confirmed="closeCockpit()"
    />
    <LeaveConfirmationPopup
      :show="showStartPopup"
      :size="POPUP.Size.small"
      :title="tv('startNewSearch', 'ORingConfigurator')"
      :content="tv('startNewSearchContent', 'ORingConfigurator')"
      :stay-button-text="tv('Frontend.Button.Cancel', 'ORingConfiguratorImport')"
      :leave-button-text="tv('startNewSearch', 'ORingConfigurator')"
      @update:show="showStartPopup = $event"
      @leave="startNewSearch"
    />
  </div>
</template>

<script setup lang="ts">
import Steps from '~/components/input-components/Steps.vue'
import ReactiveButton from '~/components/ReactiveButton.vue'
import ToggleSwitchSquare from '~/components/input-components/ToggleSwitchSquare.vue'
import DesignCockpitStep1 from '~/components/oring-calculator/design-cockpit/DesignCockpitStep1.vue'
import DesignCockpitStep2 from '~/components/oring-calculator/design-cockpit/DesignCockpitStep2.vue'
import DesignCockpitStep3 from '~/components/oring-calculator/design-cockpit/DesignCockpitStep3.vue'
import DesignCockpitStep4 from '~/components/oring-calculator/design-cockpit/DesignCockpitStep4.vue'
import useI18nExtended from '~/composables/usei18nExtended'
import NavigationClose from '~/assets/icons/navigation_close.svg?component'
import PopupConfirmDialog from '~/components/popup/PopupConfirmDialog.vue'
import LeaveConfirmationPopup from '~/components/popup/LeaveConfirmationPopup.vue'
import {POPUP} from '~/assets/js/constants'
import {useORingCalculatorStore} from '~/store/oring-calculator'
import createSeoMetaInfo from '~/assets/js/opengraph/helper'
import {getORingSeoOptions} from '~/composables/oring-helper'
import {useOringScrollToTop} from '~/composables/oring/useOringScrollToTop'
import {useOringNavigationGuard} from '~/composables/oring/useOringNavigationGuard'

// Add SEO metadata
const seoData = createSeoMetaInfo(getORingSeoOptions('design-cockpit', 'Frontend.ORing.ExpertMode', 'ORingConfiguratorImport'))
useSeoMeta(seoData)

const i18n = useI18nExtended()
const route = useRoute()
const router = useRouter()
let i18nHead = useLocaleHead({addSeoAttributes: true, route: route, router: router, i18n: i18n}).value

const head = {
  ...i18nHead,
  title: i18n.tv('pageTitle', 'ORingConfigurator')
}
useHead(head)

const {tv} = useI18nExtended()
const app = useNuxtApp()
const oringStore = useORingCalculatorStore()
const {oringCalculatorImperial, designCockpitCurrentStep, designCockpitCompletedSteps} = storeToRefs(oringStore)
const {scrollToTop} = useOringScrollToTop()

const stepComponentRef = ref<any>(null)
const showClosePopup = ref(false)

useBreadcrumb([
  {
    label: tv('Frontend.ORing.Title', 'ORingConfiguratorImport'),
    path: '/oring-calculator'
  },
  {
    label: tv('Frontend.ORing.ExpertMode', 'ORingConfiguratorImport'),
    path: '/oring-calculator/design-cockpit'
  }
])

const steps = [
  {title: tv('Frontend.Title.Applications', 'ORingConfiguratorImport'), component: DesignCockpitStep1, number: 1},
  {title: tv('Frontend.Title.Environments', 'ORingConfiguratorImport'), component: DesignCockpitStep2, number: 2},
  {title: tv('Frontend.Title.CalculationParameterPage', 'ORingConfiguratorImport'), component: DesignCockpitStep3, number: 3},
  {title: tv('results', 'ORingConfigurator'), component: DesignCockpitStep4, number: 4}
]
const showStartPopup = ref(false)
const scrolledY = ref(0)
const onScroll = () => {
  scrolledY.value = window.scrollY || document.documentElement.scrollTop || 0
}

const showFixedActionBar = computed(() => {
  return designCockpitCurrentStep.value === steps.length && scrolledY.value >= 800
})

const nextButtonText = computed(() => {
  switch (designCockpitCurrentStep.value) {
  case 3:
    return tv('showResults', 'ORingConfigurator')
  case 4:
    return tv('startNewSearch', 'ORingConfigurator')
  default:
    return tv('Frontend.Wizard.NextButtonText', 'ORingConfiguratorImport')
  }
})

const isNextButtonDisabled = computed(() => {
  // For step 4 (last step), the button is for "Back to Start" which should always be enabled
  if (designCockpitCurrentStep.value === 4) {
    return false
  }

  // Check if current step has validation and if it passes
  const stepC = stepComponentRef.value
  if (stepC && typeof stepC.validate === 'function') {
    return !stepC.validate()
  }

  return false
})

const startNewSearch = () => {
  designCockpitCurrentStep.value = 1
  designCockpitCompletedSteps.value = []
  showStartPopup.value = false
  oringStore.resetDesignCockpitStates()
  scrollToTop('auto')
}

const goToStep = async (step: number) => {
  if (step < 1 || step > steps.length) {
    return
  }
  const stepC = stepComponentRef.value ?? {validate: () => true}
  if (step <= designCockpitCurrentStep.value) {
    designCockpitCompletedSteps.value = designCockpitCompletedSteps.value.filter((s: number) => s <= step)
  } else if (!designCockpitCompletedSteps.value.includes(step - 1)) {
    if (!(await stepC.validate?.())) {
      return
    }
    designCockpitCompletedSteps.value.push(designCockpitCurrentStep.value)
  } else if (!(await stepC.validate?.())) {
    return
  }
  designCockpitCurrentStep.value = step
  scrollToTop('auto')
}

const handleNext = async () => {
  if (designCockpitCurrentStep.value < steps.length) {
    await goToStep(designCockpitCurrentStep.value + 1)
  } else {
    // Reset for new design
    designCockpitCurrentStep.value = 1
    designCockpitCompletedSteps.value = []
    scrollToTop('auto')
  }
}

const {confirmLeave, clearPendingRoute} = useOringNavigationGuard(
  router,
  showClosePopup,
  '/oring-calculator/design-cockpit',
  ['/oring-calculator/oring-assistant/details']
)

const closeCockpit = () => {
  showClosePopup.value = false
  oringStore.resetDesignCockpitStates()

  const pendingRoute = confirmLeave()
  if (pendingRoute) {
    router.push(pendingRoute)
    clearPendingRoute()
  } else {
    navigateTo(app.$globalization.localePath('/oring-calculator'))
  }
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, {passive: true})
  onScroll()
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
})
</script>

<style lang="scss">
.prompt--discard {
  .popup-confirmation-dialog__body {
    font-weight: 300;
  }
}

.design_cockpit {
  margin: 0 2rem;
  min-height: 35rem;

  @include breakpoint-down($md) {
    margin: 0 1rem;
  }

  // Add bottom space only when the fixed action bar is visible
  &.has-fixed-actionbar {
    @include breakpoint-down($md) {
      padding-bottom: 7rem; // approx. bar height + spacing
    }
  }

  .k-button-solid-light {
    background-color: $color__fst__blue__010;
    color: $color__fst__primary;
  }

  .oring-card {
    background: $color__white;
    box-shadow: $shadow-default;
    padding: 1rem;
    min-width: 16.25rem;
    flex: 1 1 0;
    max-width: 21.875rem;
    display: flex;
    flex-direction: column;

    .inputs-radio-group {
      gap: 1rem;
    }
  }

  h2 {
    margin: 0;
    margin-block: 0;
    font-weight: $base-text-font-weight-semi-bold;
  }

  .inputs-radio-button-centered img {
    max-height: 3.5rem;
  }

  &__steps {
    margin: 1rem 0;
  }

  .h1--underline {
    margin: 0 0 2rem;
  }

  // Match mobile h1 spacing with oring-assistant: remove extra margin on small screens
  @include breakpoint-down($md) {
    .h1--underline {
      margin: 0 0 1rem;
    }
  }

  &__heading {
    display: flex;
    justify-content: space-between;
    align-items: center;

    div {
      display: flex;
      align-items: center;
      cursor: pointer;
    }

    svg.icon--svg {
      color: $color__primary;
      width: 1.875rem;
    }
  }

  &__subheading {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    flex-direction: row;

    @include breakpoint-down($md) {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }

    .h3 {
      margin: 0;

      @include breakpoint-down($md) {
        margin-bottom: 1.5rem;
      }
    }

    > div {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      flex-direction: row;
      gap: 1rem;

      @include breakpoint-down($md) {
        align-items: flex-start;
      }
    }

    // extra spacing above the unit toggle (consolidated)
    @include breakpoint-down($md) {
      .toggle-switches {
        margin-top: 1.75rem;
      }
    }
  }

  .buttons {
    display: flex;
    justify-content: space-between;
    gap: 1rem;

    &.flex-end {
      justify-content: flex-end;
    }
  }

  // Mobile fixed action bar wrapper
  .mobile-buttons-wrapper {
    @include breakpoint-down($md) {
      // Default: stay in normal flow on mobile
      position: static;

      &.is-fixed {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        box-sizing: border-box;
        background: $color__white;
        padding: 1rem;
        padding-bottom: calc(1rem + env(safe-area-inset-bottom));

        /* stylelint-disable-next-line */
        box-shadow: 0 -.1rem 0.25rem rgba(0, 0, 0, 16%);
        z-index: 100;
      }
    }
  }

  .popup-content {
    font-weight: $base-text-font-weight-light;

    .popup-buttons {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
      padding-top: 1rem;
    }
  }

  .popup-confirmation-dialog {
    margin: 0;

  }

  h5 {
    font-size: 1.125rem;
    margin: -.5rem 0 1rem;
  }

   @include breakpoint-down($sm) {
    margin: 0 1rem;
  }
}
</style>
